import { createSelector } from 'reselect'
import type { State } from 'types/redux'
import type { State as SearchState } from '@disco/state-management/reducers/search'
import { getNativeTags } from '@disco/state-management/selectors/tags'
import { getCustomFieldsConfig } from '@disco/state-management/selectors/user'
import {
  additionalMetadataKeys,
  getDropdownOptions,
} from '@disco/utils/metadata'

/**
 * Gets the search state from the Redux state.
 * @param state - The Redux state
 * @returns The search state object
 */
const getSearchState = (state: State) => state.search

/**
 * Gets the channels search state from the Redux state.
 * @param state - The Redux state
 * @returns The channels search state object
 */
const getChannelsSearchState = (state: State) => state.search.channelsSearch

/**
 * Gets the metadata keys from the search state.
 * @param state - The Redux state
 * @returns Array of metadata keys
 */
const getMetadataKeys = (state: State) => getSearchState(state).metadataKeys

/**
 * Gets the search height from the search state.
 * @param state - The Redux state
 * @returns The search height value, or null/undefined if not set
 */
export const getSearchHeight = (state: State): number | null | undefined =>
  getSearchState(state).searchHeight
/**
 * Gets the user's recent search query history.
 * @param state - The Redux state
 * @returns Array of recent search query references
 */
export const getSearchHistory = (state: State) =>
  getSearchState(state).searchQueryHistory
/**
 * Gets search metadata keys enhanced with custom field configurations.
 * @param state - The Redux state
 * @returns Array of metadata keys with dropdown options from custom fields config
 */
export const getSearchMetadataKeys = createSelector(
  [getMetadataKeys, getCustomFieldsConfig],
  (metadataKeys, fieldsConfig) => {
    return metadataKeys.map(key => {
      if (key.type === 'dropdown_multi' || key.type === 'dropdown_single') {
        if (fieldsConfig[key.key]) return { ...key, ...fieldsConfig[key.key] }
      }

      return key
    })
  },
)
/**
 * Gets the complete search state with enhanced metadata keys.
 * @param state - The Redux state
 * @returns Search state object with metadata keys enhanced from custom fields config
 */
export const getSearch = createSelector(
  [getSearchState, getSearchMetadataKeys],
  (search, metadataKeys) => {
    return { ...search, metadataKeys }
  },
)
/**
 * Gets dropdown options for report metadata fields.
 * @param state - The Redux state
 * @returns Array of dropdown options for report metadata configuration
 */
export const getReportOptions = createSelector(
  [getSearchMetadataKeys],
  metadataKeys => {
    return getDropdownOptions(metadataKeys, additionalMetadataKeys, [
      'disco_comment',
      'BPM',
    ])
  },
)
/**
 * Gets the tags associated with the current search facet.
 * @param state - The Redux state
 * @returns Array of tag objects that match the search facet IDs
 */
export const getSearchFacetTags = createSelector(
  [getSearchState, getNativeTags],
  (search, tags) => {
    if (!search.facet) return []
    return search.facet
      .map(externalTagId => tags.find(t => t.id === externalTagId))
      .filter(Boolean)
  },
)
/**
 * Gets the search term for channels search.
 * @param state - The Redux state
 * @returns The channels search term string
 */
export const getChannelsSearchTerm = (state: State): string =>
  getChannelsSearchState(state).searchTerm
/**
 * Gets the inbox search state.
 * @param state - The Redux state
 * @returns The inbox search state object
 */
export const getInboxSearchState = (state: State): SearchState['inboxSearch'] =>
  state.search.inboxSearch
/**
 * Gets the recipient search state.
 * @param state - The Redux state
 * @returns The recipient search state object
 */
export const getRecipientSearchState = (
  state: State,
): SearchState['recipientSearch'] => state.search.recipientSearch
